import {
  Phone,
  Tag,
  ChevronUp,
  MessageSquare,
  CheckCircle2,
  Award,
  Users,
  ShieldCheck,
  MapPin,
} from "lucide-react";

import { useState, useRef, useEffect, Suspense, lazy } from "react";

import { motion, AnimatePresence } from "framer-motion";

const SolutionsGrid = lazy(() => import("@/components/SolutionsGrid"));
const QuoteForm = lazy(() => import("@/components/QuoteForm"));
const WhyChooseUs = lazy(() => import("@/components/WhyChooseUs"));
const Products = lazy(() => import("./Products"));
const Footer = lazy(() => import("@/components/Footer"));
const FloatingContactBar = lazy(() => import("@/components/FloatingContactBar"));

const packageTags = [
  "Tent Rental Uae",
  "Event Tent Rental Uae",
  "Industrial Tents Uae",
  "Storage Tent Rental Uae",
  "Tent Manufacturers Uae",
  "Exhibition Tent Rental Uae",
  "Tent Supplier Uae",
  "Tent Rental Abu Dhabi",
  "Tent Rentals Near Me",
  "Tents And Shades Companies Uae",
  "Wedding Rentals Uae",
  "Tents And Shades Uae",
  "Tent Supplier Dubai",
  "Tent Rental Near Me",
  "Tent Manufacturers In Dubai",
  "Tent Suppliers Dubai",
  "Tent Suppliers In Dubai",
  "Tent Supplier Abu Dhabi",
  "Emirates Tents",
  "Emirates Tents And Structure",
  "Tents For Sale",
  "Air Conditioned Tent Rental",
  "Tents For Sale In Dubai",
  "Outdoor Tent Rental",
  "Used Tent For Sale In Uae",
];

const Index = () => {
  const formRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkSize = () => setIsDesktop(window.innerWidth >= 768);
    checkSize();

    const handleScroll = () => setShowScrollTop(window.scrollY > 500);

    window.addEventListener("resize", checkSize, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", checkSize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollToFormAndFocus = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="bg-black min-h-screen relative font-sans text-white overflow-x-hidden">
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            onClick={scrollToTop}
            aria-label="Back to top"
            className={`fixed ${
              isDesktop ? "bottom-12 right-8" : "bottom-24 right-6"
            } z-[100] w-12 h-12 rounded-full bg-[#080808] border border-white/10 flex items-center justify-center shadow-xl active:scale-95 transition-transform`}
          >
            <ChevronUp className="w-6 h-6 text-white" />
          </motion.button>
        )}
      </AnimatePresence>

      <section className="relative min-h-[76vh] md:min-h-[90vh] w-full overflow-hidden flex flex-col bg-black">
        <img
          src="https://res.cloudinary.com/dizdlq9fo/image/upload/v1777547155/Artboard_186_fili8m.webp"
          alt="BA Tents industrial and event tent solutions"
          fetchPriority="high"
          loading="eager"
          className="hidden md:block absolute inset-0 w-full h-full object-cover z-0"
        />

        <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-black via-black/70 to-black/20 z-[1]" />
        <div className="absolute inset-0 hidden md:block bg-gradient-to-b from-black/20 via-transparent to-black z-[2]" />

        <div className="flex-1 relative z-10 flex flex-col items-center justify-center px-4 md:px-0 pt-2 md:pt-6">
          <div className="w-full max-w-[1440px] grid grid-cols-1 md:grid-cols-2 gap-8 items-center md:px-12">
            <div className="w-full mb-6 p-4 md:p-0 md:-ml-12 bg-transparent">
              <HeroUIContent
                scrollToFormAndFocus={scrollToFormAndFocus}
                isDesktop={isDesktop}
              />
            </div>

            <div className="hidden md:block" />
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="h-40 bg-black" />}>
        <main className="relative z-10 px-0 md:px-0 pt-0 pb-4 max-w-none mx-auto -mt-10 md:-mt-8">
          <SolutionsGrid />

          <section
            ref={formRef}
            className="w-full max-w-7xl mx-auto px-4 md:px-6 py-1 md:py-14 scroll-mt-24"
          >
            <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12">
              <p className="hidden md:block text-[#bd6aff] text-[10px] md:text-xs font-black uppercase tracking-[0.32em] mb-3">
                Start Your Project
              </p>
              <h2 className="hidden md:block text-3xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] mb-5">
                Get a Custom <span className="text-[#bd6aff]">Tent Quote</span>
              </h2>
              <p className="hidden md:block text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
                Share your rental, sale, event, exhibition, warehouse, or pavilion requirements and our team will respond quickly.
              </p>
            </div>

            <div className="rounded-2xl bg-[#080808] p-4 md:p-6">
              <QuoteForm />
            </div>
          </section>

          <Products />

          <WhyChooseUs onQuoteClick={scrollToFormAndFocus} />

          <SearchablePackages />
        </main>

        <Footer />
        <FloatingContactBar onQuoteClick={scrollToFormAndFocus} />
      </Suspense>
    </div>
  );
};

const HeroUIContent = ({
  scrollToFormAndFocus,
  isDesktop,
}: {
  scrollToFormAndFocus: () => void;
  isDesktop: boolean;
}) => (
  <>
    <div className="mt-16 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-3">
      <CheckCircle2 className="w-3.5 h-3.5 text-[#bd6aff]" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-white">
        Leading Tent Manufacturer <span className="text-[#bd6aff]">in UAE</span>
      </span>
    </div>

    <h1 className="text-[36px] md:text-[68px] font-black text-white leading-[1.05] mb-5 tracking-tight uppercase">
      Industrial & <br />
      Event Tent <br />
      Solutions in <span className="text-[#bd6aff]">UAE</span>
    </h1>

    <p className="text-zinc-300 text-sm md:text-lg mb-7 font-medium max-w-xl leading-relaxed">
      Manufacturing, sale, rental, and full setup for events, exhibitions, warehouses,
      sports facilities, pavilions, and temporary structures across UAE.
      <br />
      <span className="text-[#bd6aff] font-black">Get response in 15 minutes.</span>
    </p>

    <div className="flex flex-row items-center gap-1.5 md:gap-3 mb-8 w-full max-w-xl">
      {/* GET QUOTE */}
      <motion.button
        whileTap={{ scale: 0.96 }}
        onClick={scrollToFormAndFocus}
        className="flex-1 h-[52px] rounded-2xl border border-[#8b31ff]/50 bg-[#12061f]/70 backdrop-blur-xl text-white font-black text-[10px] md:text-xs tracking-[0.12em] uppercase flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(139,49,255,0.12)] hover:border-[#bd6aff] hover:bg-[#1a0b2b]/80 transition-all duration-300"
      >
        <Tag className="w-3.5 h-3.5 text-white" />
        <span className="whitespace-nowrap">Get Quote</span>
      </motion.button>

      {/* CALL NOW */}
      <motion.a
        whileTap={{ scale: 0.96 }}
        href="tel:+971588243660"
        className="flex-1 h-[52px] rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-xl text-white font-black text-[10px] md:text-xs tracking-[0.12em] uppercase flex items-center justify-center gap-2 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300"
      >
        <Phone className="w-3.5 h-3.5 text-white" />
        <span className="whitespace-nowrap">Call Now</span>
      </motion.a>

      {/* WHATSAPP */}
      <motion.a
        whileTap={{ scale: 0.96 }}
        href="https://wa.me/971588243660"
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 h-[52px] rounded-2xl border border-emerald-500/40 bg-emerald-500/10 backdrop-blur-xl text-[#19e68c] font-black text-[10px] md:text-xs tracking-[0.12em] uppercase flex items-center justify-center gap-2 hover:bg-emerald-500/15 hover:border-emerald-400 transition-all duration-300"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        <span className="whitespace-nowrap">WhatsApp</span>
      </motion.a>
    </div>

    {!isDesktop && (
      <div className="relative w-full aspect-[800/540] overflow-hidden rounded-2xl bg-zinc-900">
        <img
          src="https://res.cloudinary.com/dizdlq9fo/image/upload/v1777549911/Artboard_161_copy_bgndap.webp"
          fetchPriority="high"
          loading="eager"
          alt="BA Tents showcase"
          className="w-full h-full object-cover"
        />
      </div>
    )}
  </>
);

const SearchablePackages = () => (
  <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-14">
    <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12">
      <p className="text-[#bd6aff] text-[10px] md:text-xs font-black uppercase tracking-[0.32em] mb-3">
        Searchable
      </p>

      <h2 className="text-3xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] mb-5">
        Popular <span className="text-[#bd6aff]">Packages</span>
      </h2>

      <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl mx-auto">
        Quick access to our most searched tent rental, sale, manufacturing, and installation services.
      </p>
    </div>

    <div className="rounded-2xl bg-[#080808] p-4 md:p-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 md:gap-2">
        {packageTags.map((tag) => (
          <div
            key={tag}
            className="px-3 py-3 rounded-xl bg-white/[0.04] flex items-center justify-center text-center hover:bg-white/[0.07] transition-colors"
          >
            <span className="text-[10px] md:text-xs text-zinc-400 font-bold">
              {tag}
            </span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Index;
